import { GroceryCategory, type GroceryItem } from '../../types/groceryList';
import { categorizeIngredient } from '../../services/groceryListService';

/**
 * Display-level grouping of the 9 internal GroceryCategory values into the
 * ~5 store aisles ("rayons") shown on the grocery screen. The enum and
 * persisted data are unchanged — this is presentation only. Labels are
 * localized via `labelKey` (groceryList.rayons.* in the locale files);
 * `defaultLabel` is the French fallback.
 */
export interface Rayon {
  key: string;
  labelKey: string;
  defaultLabel: string;
  sortOrder: number;
}

const RAYONS: Record<string, Rayon> = {
  produce: { key: 'produce', labelKey: 'groceryList.rayons.produce', defaultLabel: 'Fruits & légumes', sortOrder: 1 },
  meat: { key: 'meat', labelKey: 'groceryList.rayons.meat', defaultLabel: 'Viande & poisson', sortOrder: 2 },
  fresh: { key: 'fresh', labelKey: 'groceryList.rayons.fresh', defaultLabel: 'Frais', sortOrder: 3 },
  grocery: { key: 'grocery', labelKey: 'groceryList.rayons.grocery', defaultLabel: 'Épicerie', sortOrder: 4 },
  other: { key: 'other', labelKey: 'groceryList.rayons.other', defaultLabel: 'Autre', sortOrder: 5 },
};

const CATEGORY_TO_RAYON: Record<GroceryCategory, Rayon> = {
  [GroceryCategory.Produce]: RAYONS.produce,
  [GroceryCategory.Meat]: RAYONS.meat,
  [GroceryCategory.Dairy]: RAYONS.fresh,
  [GroceryCategory.Frozen]: RAYONS.fresh,
  [GroceryCategory.Bakery]: RAYONS.grocery,
  [GroceryCategory.Pantry]: RAYONS.grocery,
  [GroceryCategory.Spices]: RAYONS.grocery,
  [GroceryCategory.Beverages]: RAYONS.grocery,
  [GroceryCategory.Other]: RAYONS.other,
};

export function rayonOf(category: GroceryCategory): Rayon {
  return CATEGORY_TO_RAYON[category] ?? RAYONS.other;
}

/**
 * The rayon a row is shown in.
 *
 * The category is saved when the list is generated, so a list made before #772
 * keeps oignon and poulet under « Autre » until it is regenerated. A generated
 * row stored as « Autre » is worked out again from its name; any other stored
 * category is kept, because generation read the recipe line ("4 brins de
 * thym") and the row only carries the cleaned name. A row the cook added by
 * hand keeps the category they picked.
 */
export function rayonOfItem(item: Pick<GroceryItem, 'name' | 'original' | 'category' | 'isCustom'>): Rayon {
  if (item.isCustom || item.category !== GroceryCategory.Other) return rayonOf(item.category);
  const name = item.name || item.original || '';
  return rayonOf(name ? categorizeIngredient(name) : item.category);
}
